/**
 * @nanokart/tryon-studio-sdk
 * InventoryCarousel - Arc coverflow carousel with CSS transitions (no framer-motion)
 */

import React, { useCallback, useRef } from 'react';
// NOTE: File upload uses a plain <input type="file"> — no extra deps.
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow } from 'swiper/modules';
import type { Swiper as SwiperClass } from 'swiper/types';

import 'swiper/css';
import 'swiper/css/effect-coverflow';

import { useTryOnConfig } from '../../core/TryOnProvider';
import { useInventory } from '../../core/hooks/useInventory';
import { useTryOn } from '../../core/hooks/useTryOn';
import { useProviderCapabilities } from '../../core/hooks/useProviderCapabilities';
import type { CatalogItem } from '../../types/config';

export interface InventoryCarouselProps {
  className?: string;
  style?: React.CSSProperties;
  renderItem?: (item: CatalogItem, isSelected: boolean, onSelect: () => void) => React.ReactNode;
  /** Override items list (e.g., client-side filtered results). Falls back to store items. */
  overrideItems?: CatalogItem[];
  /** When provided, shows a camera button (inside the "Add Your Dress" tile) to capture a dress live. */
  onCameraOpen?: () => void;
  /** When provided, shows an upload button (inside the "Add Your Dress" tile). Receives the selected File. */
  onUploadFile?: (file: File) => void;
  /** When provided, called if the user clicks the already-selected (center) garment card — parent can open a fullscreen preview. */
  onExpandSelected?: (item: CatalogItem) => void;
}

function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '37,99,235';
  return `${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(result[3], 16)}`;
}

interface GarmentCardProps {
  item: CatalogItem;
  isSelected: boolean;
  hasTriedOn: boolean;
  primaryColor: string;
  onSelect: () => void;
}

// Add-Your-Dress tile — same footprint as an unselected dress card, exposes
// two equally-prominent actions: Snap (camera) and Upload (device gallery).
const AddDressTile: React.FC<{
  onCameraClick?: () => void;
  onUploadClick?: () => void;
  primaryColor: string;
}> = ({ onCameraClick, onUploadClick, primaryColor }) => {
  const showCamera = !!onCameraClick;
  const showUpload = !!onUploadClick;
  return (
    <div
      data-testid="add-dress-tile"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        userSelect: 'none',
        width: 112, flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 112,
          aspectRatio: '3/4',
          borderRadius: '12px',
          border: `2px dashed ${primaryColor}55`,
          backgroundColor: `${primaryColor}08`,
          boxSizing: 'border-box',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 10,
          padding: '10px 6px',
          transition: 'border-color 0.2s, background-color 0.2s',
        }}
      >
        {/* Action buttons row */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center' }}>
          {showCamera ? (
            <button
              type="button"
              onClick={onCameraClick}
              aria-label="Snap a dress with camera"
              data-testid="dress-camera-btn"
              style={{
                width: 44, height: 44, borderRadius: '50%',
                backgroundColor: '#fff', border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', padding: 0,
                WebkitTapHighlightColor: 'transparent',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.94)'; }}
              onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
              onTouchStart={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.94)'; }}
              onTouchEnd={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </button>
          ) : null}

          {showUpload ? (
            <button
              type="button"
              onClick={onUploadClick}
              aria-label="Upload a dress photo from your device"
              data-testid="dress-upload-btn"
              style={{
                width: 44, height: 44, borderRadius: '50%',
                backgroundColor: '#fff', border: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', padding: 0,
                WebkitTapHighlightColor: 'transparent',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              }}
              onMouseDown={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.94)'; }}
              onMouseUp={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
              onTouchStart={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.94)'; }}
              onTouchEnd={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)'; }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={primaryColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </button>
          ) : null}
        </div>

        {/* Action labels */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center' }}>
          {showCamera ? (
            <span style={{ width: 44, textAlign: 'center', fontSize: '10px', fontWeight: 600, color: primaryColor }}>Snap</span>
          ) : null}
          {showUpload ? (
            <span style={{ width: 44, textAlign: 'center', fontSize: '10px', fontWeight: 600, color: primaryColor }}>Upload</span>
          ) : null}
        </div>
      </div>
      <div style={{ marginTop: 8, textAlign: 'center', width: '100%' }}>
        <p style={{ margin: 0, fontSize: '11px', fontWeight: 600, color: '#334155' }}>Add Your Dress</p>
        <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#64748b' }}>Try any dress live</p>
      </div>
    </div>
  );
};

const GarmentCard: React.FC<GarmentCardProps> = ({ item, isSelected, hasTriedOn, primaryColor, onSelect }) => (
  <div
    onClick={onSelect}
    data-testid={`garment-card-${item.productId}`}
    style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      cursor: 'pointer', userSelect: 'none',
      width: 140,
      transform: isSelected ? 'scale(1) translateY(0px)' : 'scale(0.85) translateY(12px)',
      opacity: isSelected ? 1 : 0.72,
      transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.28s ease',
    }}
  >
    {/* Image container */}
    <div
      style={{
        position: 'relative',
        width: 140,
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: '#f8fafc',
        boxShadow: isSelected
          ? `0 8px 24px rgba(0,0,0,0.12), 0 0 0 2px rgba(${hexToRgb(primaryColor)},0.5)`
          : '0 2px 8px rgba(0,0,0,0.08)',
        transition: 'box-shadow 0.2s',
        aspectRatio: '3/4',
      }}
    >
      <img
        src={item.thumbnailUrl || item.imageUrl}
        alt={item.name}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        loading="lazy"
      />

      {/* Tried indicator */}
      {hasTriedOn ? (
        <div
          style={{
            position: 'absolute', top: 8, right: 8,
            width: 22, height: 22,
            backgroundColor: '#10b981', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
            <polyline points="20,6 9,17 4,12" />
          </svg>
        </div>
      ) : null}

      {/* Info icon on active */}
      {isSelected ? (
        <div
          style={{
            position: 'absolute', top: 8, left: 8,
            width: 22, height: 22,
            backgroundColor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(4px)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
      ) : null}
    </div>

    {/* Product info */}
    <div style={{ marginTop: 8, textAlign: 'center', width: '100%', padding: '0 4px' }}>
      <p
        style={{
          margin: 0, fontSize: '11px', fontWeight: 600,
          color: isSelected ? primaryColor : '#334155',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          transition: 'color 0.2s', maxWidth: 132,
        }}
        title={item.name}
      >
        {item.name}
      </p>
      <p style={{ margin: '2px 0 0', fontSize: '10px', color: '#64748b' }}>
        {item.metadata?.isCameraCapture
          ? 'Captured just now'
          : `${item.currency} ${item.price.toLocaleString()}`}
      </p>
    </div>
  </div>
);

export const InventoryCarousel: React.FC<InventoryCarouselProps> = ({
  className,
  style,
  renderItem,
  overrideItems,
  onCameraOpen,
  onUploadFile,
  onExpandSelected,
}) => {
  const { config } = useTryOnConfig();
  const { items: storeItems, selectedItem, selectItem, isLoading, hasMore, loadMore } = useInventory();
  const items = overrideItems ?? storeItems;
  const { hasTriedOn } = useTryOn();
  const { forceSingleItemView, showInventoryCarousel } = useProviderCapabilities();
  const swiperRef = useRef<SwiperClass | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  const primaryColor = config.brandingConfig?.primaryColor ?? '#2563eb';

  const handleSelect = useCallback((item: CatalogItem) => {
    // If tapping the already-centered card, treat as "expand to full-screen"
    // (parent must provide onExpandSelected — otherwise falls back to select).
    if (onExpandSelected && selectedItem?.productId === item.productId) {
      onExpandSelected(item);
      return;
    }
    selectItem(item);
  }, [selectItem, selectedItem, onExpandSelected]);

  const handleUploadClick = useCallback(() => {
    uploadInputRef.current?.click();
  }, []);

  const handleUploadChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset immediately so selecting the same file again re-fires change
    e.target.value = '';
    if (file && onUploadFile) onUploadFile(file);
  }, [onUploadFile]);

  const handleSlideChange = useCallback((swiper: SwiperClass) => {
    const item = items[swiper.activeIndex];
    if (item) selectItem(item);
    // Trigger loadMore when approaching the end, but only for unfiltered list
    if (!overrideItems && hasMore && !isLoading && items.length > 3 && swiper.activeIndex >= items.length - 3) {
      loadMore();
    }
  }, [items, overrideItems, selectItem, hasMore, isLoading, loadMore]);

  if (forceSingleItemView || !showInventoryCarousel) return null;

  return (
    <div
      className={className}
      style={{ width: '100%', paddingBottom: '8px', ...style }}
      data-testid="inventory-carousel"
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        {/* Add-Your-Dress tile — Snap + Upload actions, same size as dress cards */}
        {(onCameraOpen || onUploadFile) ? (
          <div style={{ paddingTop: 10, paddingLeft: 4 }}>
            <AddDressTile
              onCameraClick={onCameraOpen}
              onUploadClick={onUploadFile ? handleUploadClick : undefined}
              primaryColor={primaryColor}
            />
            {onUploadFile ? (
              <input
                ref={uploadInputRef}
                type="file"
                accept="image/*"
                onChange={handleUploadChange}
                data-testid="dress-upload-input"
                style={{ display: 'none' }}
              />
            ) : null}
          </div>
        ) : null}

        <div style={{ flex: 1, minWidth: 0 }}>
      {/* Loading State */}
      {isLoading && items.length === 0 ? (
        <div
          style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            height: '200px', gap: '10px', color: '#94a3b8', fontSize: '13px',
          }}
          data-testid="carousel-loading"
        >
          <div
            style={{
              width: 18, height: 18,
              border: `2px solid ${primaryColor}30`,
              borderTopColor: primaryColor,
              borderRadius: '50%',
              animation: 'nk-spin 0.8s linear infinite',
            }}
          />
          Loading catalog...
        </div>
      ) : null}

      {/* Empty State */}
      {!isLoading && items.length === 0 ? (
        <div
          style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            height: '180px', color: '#94a3b8', fontSize: '13px',
          }}
          data-testid="carousel-empty"
        >
          No items available
        </div>
      ) : null}

      {/* Arc Carousel */}
      {items.length > 0 ? (
        <Swiper
          key={overrideItems ? `filtered-${items.length}` : `all-${items.length}`}
          modules={[EffectCoverflow]}
          effect="coverflow"
          grabCursor
          centeredSlides
          slidesPerView="auto"
          initialSlide={selectedItem ? Math.max(0, items.findIndex(i => i.productId === selectedItem.productId)) : 0}
          onSwiper={(swiper) => { swiperRef.current = swiper; }}
          onSlideChange={handleSlideChange}
          coverflowEffect={{
            rotate: 0,
            stretch: 0,
            depth: 100,
            modifier: 2,
            slideShadows: false,
          }}
          style={{ padding: '10px 0 12px' }}
          data-testid="swiper-carousel"
        >
          {items.map((item) => {
            const isSelected = selectedItem?.productId === item.productId;
            const triedOn = hasTriedOn(item.productId);

            return (
              <SwiperSlide
                key={item.productId}
                style={{ width: 'auto', cursor: 'pointer' }}
                onClick={() => {
                  const idx = items.findIndex(i => i.productId === item.productId);
                  if (swiperRef.current) swiperRef.current.slideTo(idx);
                  handleSelect(item);
                }}
              >
                {renderItem ? (
                  renderItem(item, isSelected, () => handleSelect(item))
                ) : (
                  <GarmentCard
                    item={item}
                    isSelected={isSelected}
                    hasTriedOn={triedOn}
                    primaryColor={primaryColor}
                    onSelect={() => handleSelect(item)}
                  />
                )}
              </SwiperSlide>
            );
          })}

          {/* Load More */}
          {hasMore ? (
            <SwiperSlide style={{ width: 'auto' }}>
              <div
                onClick={() => loadMore()}
                role="button"
                tabIndex={0}
                data-testid="load-more-btn"
                style={{
                  width: 100,
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 8,
                  borderRadius: '12px',
                  border: `2px dashed ${primaryColor}50`,
                  backgroundColor: `${primaryColor}08`,
                  cursor: 'pointer', color: primaryColor,
                  fontSize: '12px', fontWeight: 500,
                  aspectRatio: '3/4',
                }}
              >
                {isLoading ? (
                  <div style={{
                    width: 16, height: 16,
                    border: `2px solid ${primaryColor}30`,
                    borderTopColor: primaryColor,
                    borderRadius: '50%',
                    animation: 'nk-spin 0.8s linear infinite',
                  }} />
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    More
                  </>
                )}
              </div>
            </SwiperSlide>
          ) : null}
        </Swiper>
      ) : null}
        </div>
      </div>

      {/* Pagination counter */}
      {items.length > 0 ? (
        <div
          style={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}
          data-testid="item-count"
        >
          <span style={{ fontSize: '11px', color: '#94a3b8' }}>
            {items.length}{hasMore ? '+' : ''} items
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default InventoryCarousel;
